import { useCallback, useEffect, useReducer } from "react";

import { UserPlaySettings } from "../../redux/slices";

const defaultSettings: UserPlaySettings = {
  sizeOfArrowHeads: 1,
  accountId: 0,
  incHashPass: false,
  autoFastAdd: false,
  autoFastMotion: false,
  enbActPalette: false,
  viewCourtBgr: false,
  enbCoachPoints: false,
  viewGrid: false,
  id: "",
  createdTimestamp: "",
  updatedTimestamp: "",
  deleted: false,
  colors: [],
};

enum PlaySettingsAction {
  UPDATE_SETTINGS = "UPDATE_SETTINGS",
  ARROW_SIZE = "ARROW_SIZE",
  AUTO_FAST_ADD = "AUTO_FAST_ADD",
  AUTO_FAST_MOTION = "AUTO_FAST_MOTION",
  VIEW_COURT_BACKGROUND = "VIEW_COURT_BACKGROUND",
  ENABLE_ACTION_PALETTE = "ENABLE_ACTION_PALETTE",
  INCLUDE_HASH_PASS = "INCLUDE_HASH_PASS",
  VIEW_GRID = "VIEW_GRID",
  ENABLE_COACHING_POINTS = "ENABLE_COACHING_POINTS",
}

type Action =
  | { type: PlaySettingsAction.UPDATE_SETTINGS; payload: UserPlaySettings }
  | { type: PlaySettingsAction.ARROW_SIZE; payload: UserPlaySettings["sizeOfArrowHeads"] }
  | { type: PlaySettingsAction.AUTO_FAST_ADD }
  | { type: PlaySettingsAction.AUTO_FAST_MOTION }
  | { type: PlaySettingsAction.VIEW_COURT_BACKGROUND }
  | { type: PlaySettingsAction.ENABLE_ACTION_PALETTE }
  | { type: PlaySettingsAction.INCLUDE_HASH_PASS }
  | { type: PlaySettingsAction.VIEW_GRID }
  | { type: PlaySettingsAction.ENABLE_COACHING_POINTS };

const reducer = (state: UserPlaySettings, action: Action) => {
  switch (action.type) {
    case PlaySettingsAction.UPDATE_SETTINGS:
      return { ...state, ...action.payload };

    case PlaySettingsAction.ARROW_SIZE:
      return { ...state, sizeOfArrowHeads: action.payload };

    case PlaySettingsAction.AUTO_FAST_ADD:
      return { ...state, autoFastAdd: !state.autoFastAdd };

    case PlaySettingsAction.AUTO_FAST_MOTION:
      return { ...state, autoFastMotion: !state.autoFastMotion };

    case PlaySettingsAction.VIEW_COURT_BACKGROUND:
      return { ...state, viewCourtBgr: !state.viewCourtBgr };

    case PlaySettingsAction.ENABLE_ACTION_PALETTE:
      return { ...state, enbActPalette: !state.enbActPalette, ...(!state.enbActPalette ? { incHashPass: false } : {}) };

    case PlaySettingsAction.INCLUDE_HASH_PASS:
      return { ...state, incHashPass: !state.incHashPass, ...(!state.incHashPass ? { enbActPalette: false } : {}) };

    case PlaySettingsAction.VIEW_GRID:
      return { ...state, viewGrid: !state.viewGrid };

    case PlaySettingsAction.ENABLE_COACHING_POINTS:
      return { ...state, enbCoachPoints: !state.enbCoachPoints };

    default:
      return state;
  }
};

export interface UseSettignsReducerProps {
  initialSettings: UserPlaySettings | undefined;
}

export const useSettingsReducer = ({ initialSettings = defaultSettings }: UseSettignsReducerProps) => {
  const [settings, dispatch] = useReducer(reducer, initialSettings);

  useEffect(() => {
    if (initialSettings) {
      dispatch({ type: PlaySettingsAction.UPDATE_SETTINGS, payload: initialSettings });
    }
  }, [initialSettings]);

  const resetState = useCallback(() => {
    dispatch({ type: PlaySettingsAction.UPDATE_SETTINGS, payload: initialSettings });
  }, [initialSettings]);

  const setArrowSize = (size: UserPlaySettings["sizeOfArrowHeads"]) => {
    dispatch({ type: PlaySettingsAction.ARROW_SIZE, payload: size });
  };

  const toggleAutoFastAdd = () => {
    dispatch({ type: PlaySettingsAction.AUTO_FAST_ADD });
  };

  const toggleAutoFastMotion = () => {
    dispatch({ type: PlaySettingsAction.AUTO_FAST_MOTION });
  };

  const toggleViewCourtBackground = () => {
    dispatch({ type: PlaySettingsAction.VIEW_COURT_BACKGROUND });
  };

  const toggleEnableActionPalette = () => {
    dispatch({ type: PlaySettingsAction.ENABLE_ACTION_PALETTE });
  };

  const toggleIncludeHashPass = () => {
    dispatch({ type: PlaySettingsAction.INCLUDE_HASH_PASS });
  };

  const toggleViewGrid = () => {
    dispatch({ type: PlaySettingsAction.VIEW_GRID });
  };

  const toggleEnableCoachingPoints = () => {
    dispatch({ type: PlaySettingsAction.ENABLE_COACHING_POINTS });
  };

  return {
    settings,
    resetState,
    setArrowSize,
    toggleAutoFastAdd,
    toggleAutoFastMotion,
    toggleViewCourtBackground,
    toggleEnableActionPalette,
    toggleIncludeHashPass,
    toggleViewGrid,
    toggleEnableCoachingPoints,
  };
};

export type SettingsReducer = ReturnType<typeof useSettingsReducer>;
